import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { graphql } from 'react-apollo';
import cx from 'classnames';

import confirmVehicleReturnMutation from './confirmVehicleReturn.graphql';
import showToaster from '../../helpers/toasterMessages/showToaster';
import messages from '../../locale/messages';
import cs from '../commonStyle.css';

// The host answers whether the vehicle actually came back.
//
// Photographs prove the car's condition, not that possession changed hands. When
// the renter uploaded the after-scan, the trip waits here until the host says
// yes or no, or until the confirmation window runs out.
//
// Renders nothing unless the API says this reservation is awaiting an answer, so
// it can be mounted unconditionally wherever a host sees their trips.
class ReturnConfirmation extends React.Component {
  static propTypes = {
    reservationId: PropTypes.number.isRequired,
    // 'not_required' | 'awaiting' | 'confirmed' | 'disputed' | 'expired'
    returnConfirmationStatus: PropTypes.string,
    // Guest must have submitted End Ride before the host can answer.
    rider: PropTypes.string,
    confirmVehicleReturn: PropTypes.func.isRequired,
    // Called after a successful answer so the parent can refetch. Optional:
    // without it the row simply looks stale until the next load, which is
    // better than this component reaching into someone else's query.
    onAnswered: PropTypes.func,
  };

  state = { submitting: false };

  answer = async (received) => {
    const { reservationId, confirmVehicleReturn, onAnswered } = this.props;
    const { formatMessage } = this.props.intl;

    // Guarded rather than merely disabled: a double click on a slow connection
    // would otherwise send two answers, and the second gets "already answered"
    // back — a confusing error for something the host did right.
    if (this.state.submitting) return;
    this.setState({ submitting: true });

    try {
      const { data } = await confirmVehicleReturn({ variables: { reservationId, received } });
      const result = data && data.confirmVehicleReturn;

      if (result && result.status === 200) {
        // Translated like every other user-facing string here. These were
        // hardcoded English in the first draft, which would have shipped
        // untranslated to every locale the rest of this modal supports.
        showToaster({
          messageId: 'commonSuccess',
          toasterType: 'success',
          requestMessage: formatMessage(
            received ? messages.returnConfirmedToast : messages.returnDisputedToast
          ),
        });
        if (onAnswered) onAnswered();
        return;
      }

      showToaster({
        messageId: 'commonError',
        toasterType: 'error',
        // The API's message when it has one — it is specific and worth showing
        // ("already answered", "no longer awaiting confirmation") — and a
        // translated fallback when it does not.
        requestMessage: (result && result.errorMessage) || formatMessage(messages.returnAnswerFailed),
      });
    } catch (error) {
      showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: error.message });
    } finally {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { returnConfirmationStatus, rider } = this.props;
    const { submitting } = this.state;

    // Only 'awaiting' is actionable. 'not_required' is the ordinary case where
    // the host uploaded the scan themselves; the rest are already answered or
    // timed out, and re-showing the question would invite a second answer the
    // API would refuse.
    // Do not prompt before End Ride submit — API would record confirm without completing.
    if (returnConfirmationStatus !== 'awaiting' || rider !== 'end') return null;

    return (
      <div className={cx(cs.spaceTop3, cs.spaceBottom3)}>
        <p className={cx(cs.commonContentText, cs.fontWeightMedium, cs.spaceBottom2)}>
          <FormattedMessage {...messages.returnConfirmationQuestion} />
        </p>
        <p className={cx(cs.commonContentText, cs.spaceBottom2)}>
          <FormattedMessage {...messages.returnConfirmationExplain} />
        </p>
        <Button
          bsStyle="primary"
          disabled={submitting}
          onClick={() => this.answer(true)}
        >
          <FormattedMessage {...messages.returnConfirmationYes} />
        </Button>
        {' '}
        <Button
          bsStyle="default"
          disabled={submitting}
          onClick={() => this.answer(false)}
        >
          <FormattedMessage {...messages.returnConfirmationNo} />
        </Button>
      </div>
    );
  }
}

export default injectIntl(
  graphql(confirmVehicleReturnMutation, { name: 'confirmVehicleReturn' })(ReturnConfirmation)
);
